"use client";

import React, { useCallback, useRef } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import "./autocomplete-select.scss";

export interface AutocompleteSelectProps {
  id?: string;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: Array<{ value: string; id?: string }>;
  onSelectOption: (item: { value: string; id?: string }) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  renderTrailing?: (props: { clear: () => void }) => React.ReactNode;
  listId?: string;
  optionClassName?: string;
}

export function AutocompleteSelect({
  id,
  className,
  value,
  onChange,
  placeholder,
  options,
  onSelectOption,
  open,
  onOpenChange,
  onKeyDown,
  renderTrailing,
  listId = "autocomplete-select-list",
  optionClassName,
}: AutocompleteSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useClickOutside<HTMLDivElement>(() =>
    onOpenChange(false),
  );

  const handleClear = useCallback(() => {
    onChange("");
    onOpenChange(false);
    inputRef.current?.focus();
  }, [onChange, onOpenChange]);

  const handleSelect = useCallback(
    (item: { value: string; id?: string }) => {
      onSelectOption(item);
      onOpenChange(false);
      inputRef.current?.focus();
    },
    [onSelectOption, onOpenChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown" && options.length > 0) {
        e.preventDefault();
        if (!open) {
          onOpenChange(true);
          return;
        }
        const first = containerRef.current?.querySelector(
          "[data-autocomplete-option]",
        ) as HTMLElement | null;
        first?.focus();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
        inputRef.current?.focus();
      }
      onKeyDown?.(e);
    },
    [open, options.length, onOpenChange, onKeyDown, containerRef],
  );

  const handleOptionKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLLIElement>,
      item: { value: string; id?: string },
    ) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSelect(item);
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        (e.currentTarget.nextElementSibling as HTMLElement)?.focus();
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        (e.currentTarget.previousElementSibling as HTMLElement)?.focus();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
        inputRef.current?.focus();
      }
    },
    [handleSelect, onOpenChange],
  );

  const showList = open && options.length > 0;

  return (
    <div
      className={
        className ? `autocomplete-select ${className}` : "autocomplete-select"
      }
      ref={containerRef}
    >
      <div className="autocomplete-select__input-row">
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="autocomplete-select__input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onClick={() => options.length > 0 && onOpenChange(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={showList ? listId : undefined}
        />
        {renderTrailing ? (
          renderTrailing({ clear: handleClear })
        ) : value ? (
          <button
            type="button"
            className="autocomplete-select__clear"
            onClick={handleClear}
            aria-label="Clear"
          >
            ×
          </button>
        ) : null}
      </div>
      {showList && (
        <div className="autocomplete-select__dropdown">
          <ul
            id={listId}
            className="autocomplete-select__options"
            role="listbox"
            aria-label="Suggestions"
          >
            {options.map((item, i) => (
              <li
                key={`${item.value}-${i}`}
                className={
                  optionClassName
                    ? `autocomplete-select__option ${optionClassName}`
                    : "autocomplete-select__option"
                }
                role="option"
                aria-selected={false}
                tabIndex={0}
                data-autocomplete-option
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(item);
                }}
                onKeyDown={(e) => handleOptionKeyDown(e, item)}
              >
                {item.value}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
